import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import I18n from "../../i18n"
import { calculateEstimateTotals } from "./estimate_totals.mjs"
import { formatCurrency } from "./currency.mjs"

// A distinct, numeric select value keeps the submitted percentage valid while
// global_discount_type and the amount carry the actual fixed-discount choice.
const FIXED_DISCOUNT_OPTION = "0.00"
const t = (key, options) => I18n.t(`erp.sales.js.estimate_editor.${key}`, options)

// Drives the estimate editor: textile lines (with their size breakdown), markings (with the
// textile lines they are attached to), sections, and the live summary panel.
// Connects to data-controller="erp--estimate-editor"
export default class extends Controller {
  static targets = [
    "sectionsContainer", "itemsList", "itemTemplate", "sectionTemplate", "sizeTemplate",
    "allocationItemTemplate", "allocationSizeTemplate",
    "item", "section", "destroyField", "positionField", "sectionIdField", "sectionEditorKeyField",
    "sectionDestroyField", "sectionPositionField", "itemForm", "sizeList", "quantityField",
    "itemSummaryRef", "itemSummaryColor", "itemSummaryDescription", "itemSummarySizes",
    "itemSummaryUnitPrice", "itemSummaryTotal",
    "markingsList", "markingTemplate", "marking", "markingForm",
    "markingDestroyField", "markingPositionField",
    "billingAddress", "shippingAddress",
    "totalQty", "totalTextiles", "totalItemsSubtotal", "totalSubtotal", "totalGlobalDiscount",
    "totalVat", "totalTtc", "globalDiscountType", "globalDiscountPercentage", "globalDiscountAmount", "globalDiscountAmountField", "globalDiscountRow",
    "markingsBreakdown"
  ]

  static values = {
    itemIndex: Number,
    markingIndex: Number,
    sectionIndex: Number
  }

  connect() {
    this._queuedStructuralActions = []
    this._pendingTextileMarkingItems = null
    this._autosaveForm = this.element.closest("form")
    this._onAutosaveRequestFinished = this.#handleAutosaveRequestFinished.bind(this)
    this._autosaveForm?.addEventListener("erp--autosave:request-finished", this._onAutosaveRequestFinished)
    this._initSortable()
    this.#normalizeOpenMarkings()
    this._recalculateTotals()
  }

  disconnect() {
    this._autosaveForm?.removeEventListener("erp--autosave:request-finished", this._onAutosaveRequestFinished)
  }

  // ── Textile lines ────────────────────────────────────────────

  // The picker is a single modal shared by every "add a textile" button, so the button that
  // opened it decides which section the new lines land in.
  openPicker(event) {
    event.preventDefault()
    if (this.#deferStructuralAction(event, "openPicker", false)) return

    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    this._pendingReplacement = item || null
    this._pendingContainer = item?.closest("[data-erp--estimate-editor-target='itemsList']")
      || event.target.closest("[data-erp--estimate-editor-target='section']")
        ?.querySelector("[data-erp--estimate-editor-target='itemsList']")
      || event.target.closest("[data-erp--estimate-editor-target='itemsList']")
      || this._ungroupedItemsList()

    document.dispatchEvent(new CustomEvent("textile-picker:open", {
      detail: { mode: item ? "replace" : "add", hasAllocations: item ? this.#itemHasAllocations(item) : false }
    }))
  }

  replaceItem(event) {
    event.stopPropagation()
    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    if (item && this.#itemHasAllocations(item) && !window.confirm(t("replace_allocated_confirm"))) return

    this.openPicker(event)
  }

  // Called by the erp--textile-picker:items-selected event. One line per colourway, with
  // its sizes broken down inside — never one line per size.
  addSelectedItems(event) {
    if (this.#deferStructuralAction(event, "addSelectedItems")) return
    const { colours } = event.detail
    if (!colours?.length) return

    const container = this._pickerContainer()
    if (!container) return

    const replacement = this.#consumeReplacement()
    if (replacement) {
      const replacementMoves = this.#planReplacementMoves(replacement, colours)
      const preservedVariantIds = new Set(
        [...replacementMoves.values()].flat().map(move => move.sourceId)
      )
      this.#replaceCatalogueItem(replacement, colours[0], preservedVariantIds)
      colours.slice(1).forEach((colour, offset) => {
        const newItem = this.#appendItem(container)
        this.#populateCatalogueItem(newItem, colour)
        this.#bindReplacementMoves(newItem, replacementMoves.get(offset + 1) || [])
      })
      this._updatePositions(container)
      this._initSortableForList(container)
      this.syncAllocations()
      this._recalculateTotals()
      this.dispatch("changed")
      this._triggerAutosave()
      return
    }

    const newItems = colours.map(colour => {
      const newItem = this.#appendItem(container)
      this.#populateCatalogueItem(newItem, colour)
      return newItem
    })

    this._updatePositions(container)
    this._initSortableForList(container)
    this.syncAllocations()
    this._recalculateTotals()
    this.dispatch("changed")
    this.#scheduleTextileMarkingFlow(newItems)
  }

  // Called by the erp--textile-picker:custom-selected event: a garment the customer brings in,
  // so the line carries a free description instead of a catalogue reference.
  addCustomItem(event) {
    if (this.#deferStructuralAction(event, "addCustomItem")) return
    const { garment_type, garment_label, color_name, comment, sizes, quantity, description, unit_price_cents } = event.detail
    if (!sizes?.length && !quantity) return

    const container = this._pickerContainer()
    if (!container) return

    const replacement = this.#consumeReplacement()
    if (replacement) {
      this.#replaceCustomItem(replacement, event.detail)
      this.syncAllocations()
      this._recalculateTotals()
      this.dispatch("changed")
      this._triggerAutosave()
      return
    }

    const newItem = this.#appendItem(container)
    this.#populateCustomItem(newItem, {
      garment_type, garment_label, color_name, comment, sizes, quantity, description, unit_price_cents
    })

    this._updatePositions(container)
    this._initSortableForList(container)
    this.syncAllocations()
    this._recalculateTotals()
    this.dispatch("changed")
    this.#scheduleTextileMarkingFlow([ newItem ])
  }

  cancelPicker() {
    this._pendingReplacement = null
    this._pendingContainer = null
  }

  customerChanged(event) {
    const selectedOption = event.target.selectedOptions?.[0]
    const customerType = selectedOption?.dataset?.customerType
    this.#replaceAddressOptions(selectedOption)
    if (!customerType) return

    this.#visible(this.itemTargets).forEach(item => {
      const manual = item.querySelector("input[type='checkbox'][name*='[unit_price_override]']")?.checked
      const complimentary = item.querySelector("input[type='checkbox'][name*='[complimentary]']")?.checked
      const marginOverride = item.querySelector("input[name*='[textile_margin_rate]']")?.value
      if (item.dataset.customerSupplied === "true" || manual || complimentary || marginOverride) return

      item.dataset.pricingStale = "true"
      this.#refreshItemSummary(item)
    })

    this.markingTargets.forEach(marking => {
      marking.setAttribute("data-erp--marking-form-customer-type-value", customerType)
      this.application
        .getControllerForElementAndIdentifier(marking, "erp--marking-form")
        ?.customerContextChanged(customerType)
    })
    this._recalculateTotals()
  }

  #replaceAddressOptions(selectedOption) {
    let addresses = []
    try {
      addresses = JSON.parse(selectedOption?.dataset?.addresses || "[]")
    } catch (_error) {
      addresses = []
    }

    const replace = (select, defaultKey) => {
      if (!select) return
      select.replaceChildren()
      const fallback = document.createElement("option")
      fallback.value = ""
      fallback.textContent = t("customer_main_address")
      select.appendChild(fallback)
      addresses.forEach(address => {
        const option = document.createElement("option")
        option.value = address.id
        option.textContent = address.label
        option.selected = Boolean(address[defaultKey])
        select.appendChild(option)
      })
      select.disabled = !selectedOption?.value
    }

    replace(this.hasBillingAddressTarget ? this.billingAddressTarget : null, "billing_default")
    replace(this.hasShippingAddressTarget ? this.shippingAddressTarget : null, "shipping_default")
  }

  // The server returns the persisted source→target ids after moving allocations. Rebuild the
  // open allocation panels in place so their labels and hidden ids immediately reflect the new
  // price line without requiring a page reload.
  reconcileVariantReplacements(event) {
    const replacements = event.detail?.replacements || {}
    if (Object.keys(replacements).length === 0) return

    Object.entries(replacements).forEach(([sourceId, targetId]) => {
      const sourceRow = this.#sizeRowById(sourceId)
      const targetRow = this.#sizeRowById(targetId)
      const targetItem = targetRow?.closest("[data-erp--estimate-editor-target='item']")
      if (!targetRow || !targetItem) return

      this.markingTargets.forEach(marking => {
        const allocationRows = [...marking.querySelectorAll("[data-erp--marking-form-target='allocationRow']")]
          .filter(row => row.querySelector("input[name$='[estimate_item_variant_id]']")?.value === sourceId)
        if (allocationRows.length === 0) return

        const targetSection = this.#ensureAllocationItemSection(marking, targetItem)
        const targetSizes = targetSection?.querySelector("[data-allocation-sizes]")
        if (!targetSizes) return

        allocationRows.forEach(row => {
          row.querySelector("input[name$='[estimate_item_variant_id]']").value = targetId
          this.#refreshMovedAllocationRow(row, targetItem, targetRow)
          targetSizes.appendChild(row)
        })

        marking.querySelectorAll("[data-allocation-item]").forEach(section => {
          if (section !== targetSection && !section.querySelector("[data-erp--marking-form-target='allocationRow']")) {
            section.remove()
          }
        })
      })

      sourceRow?.remove()
      const replacementField = targetRow.querySelector("[data-replacement-source-variant-id]")
      if (replacementField) replacementField.value = ""
    })

    this.itemTargets.forEach(item => {
      const variantIds = new Set(
        [...item.querySelectorAll("[data-size-row] input[type='hidden'][name$='[id]']")]
          .map(input => input.value)
          .filter(Boolean)
      )
      item.dataset.hasMarkingAllocations = String(
        [...this.element.querySelectorAll("[data-erp--marking-form-target='allocationRow']")].some(row => {
          const variantId = row.querySelector("input[name$='[estimate_item_variant_id]']")?.value
          const destroyed = row.querySelector("input[name$='[_destroy]']")?.value === "1"
          return variantIds.has(variantId) && !destroyed
        })
      )
    })
  }

  removeItem(event) {
    event.preventDefault()
    event.stopPropagation()

    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    if (!item) return
    if (this.#deferStructuralAction(event, "removeItem")) {
      this.#cancelPendingTextileMarkingItem(item)
      return
    }

    const automaticMarkings = this.#automaticMarkingsAllocatedToItem(item)
    this.#discard(item, "destroyField")
    this.syncAllocations()
    this.#discardEmptyAutomaticMarkings(automaticMarkings)
    this._recalculateTotals()
    this.dispatch("changed")
    this._triggerAutosave()
  }

  moveItem(event) {
    if (this.#deferStructuralAction(event, "moveItem")) return
    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    if (!item || !this.#moveSibling(item, event.params.direction, "[data-erp--estimate-editor-target='item']")) return

    this._updatePositions(item.parentElement)
    this.dispatch("changed")
    this._triggerAutosave()
    event.currentTarget.focus()
  }

  moveItemSection(event) {
    if (this.#deferStructuralAction(event, "moveItemSection")) return
    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    if (!item) return

    const lists = this.itemsListTargets.filter(list => list.closest("[data-erp--estimate-editor-target='section']")?.style.display !== "none")
    const currentList = item.parentElement
    const currentIndex = lists.indexOf(currentList)
    const destination = lists[currentIndex + (event.params.direction === "previous" ? -1 : 1)]
    if (!destination) return

    destination.appendChild(item)
    this._updatePositions(currentList)
    this._updatePositions(destination)
    this._updateSectionIdForItem(item, destination)
    this.dispatch("changed")
    this._triggerAutosave()
    event.currentTarget.focus()
  }

  toggleItem(event) {
    event.preventDefault()
    event.stopPropagation()

    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    const form = item?.querySelector("[data-erp--estimate-editor-target='itemForm']")
    if (!form) return

    form.classList.toggle("hidden")
    item.querySelector("[data-item-toggle]")?.setAttribute("aria-expanded", String(!form.classList.contains("hidden")))
  }

  updateItemSummary(event) {
    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    if (!item) return

    this.#syncQuantity(item)
    this.#refreshItemSummary(item)
    this.syncAllocations()
    this._recalculateTotals()
  }

  // Persisted textile/size ids arrive after autosave. Resynchronizing every active marking
  // also keeps limits and labels correct when a textile quantity changes or a line disappears.
  syncAllocations() {
    this.#visible(this.markingTargets).forEach(marking => this.#syncMarkingAllocationRows(marking))
  }

  // ── Markings ─────────────────────────────────────────────────

  addMarking(event) {
    event.preventDefault()
    if (this.#deferStructuralAction(event, "addMarking")) return
    if (!this.hasMarkingsListTarget) return

    this.#closeOtherMarkings()
    const marking = this.#appendMarking()
    this.#syncMarkingAllocationRows(marking)
    this.#setMarkingOpen(marking, true)

    this._updateMarkingPositions()
    this._recalculateTotals()
    this.dispatch("changed")
    this._triggerAutosave()
  }

  // Copies a marking's configuration into a new row, leaving its textile attachments empty
  // so the user picks which other products it applies to.
  duplicateMarking(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.#deferStructuralAction(event, "duplicateMarking")) return

    const source = event.target.closest("[data-erp--estimate-editor-target='marking']")
    if (!source) return

    this.#closeOtherMarkings()
    const marking = this.#appendMarking()
    this.#syncMarkingAllocationRows(marking)
    const copied = ["marking_technique_id", "technique", "sub_technique", "marking_zone_id",
      "custom_marking_zone_name", "marking_position", "width_mm", "height_mm",
      "dimension_1", "dimension_2", "unit_price", "vat_rate", "discount_type",
      "discount_percentage", "manual_price_reason", "notes"]

    copied.forEach(name => {
      const from = source.querySelector(`[name*='[${name}]']`)
      if (from) this.#setField(marking, name, from.value)
    })
    const nextArtworkVersion = Math.max(0, ...this.markingTargets.map(row => {
      return parseInt(row.querySelector("input[name*='[artwork_version]']")?.value) || 0
    })) + 1
    this.#setField(marking, "artwork_version", String(nextArtworkVersion))

    ;["unit_price_override", "artwork_pending_from_customer"].forEach(name => {
      const from = source.querySelector(`input[type='checkbox'][name*='[${name}]']`)
      const to = marking.querySelector(`input[type='checkbox'][name*='[${name}]']`)
      if (!from || !to) return

      to.checked = from.checked
      to.dispatchEvent(new Event("change", { bubbles: true }))
    })

    this.#setMarkingOpen(marking, true)
    marking.scrollIntoView({ behavior: "smooth", block: "center" })

    this._updateMarkingPositions()
    this._recalculateTotals()
    this.dispatch("changed")
    this._triggerAutosave()
  }

  removeMarking(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.#deferStructuralAction(event, "removeMarking")) return

    const marking = event.target.closest("[data-erp--estimate-editor-target='marking']")
    if (!marking) return

    this.#discard(marking, "markingDestroyField")
    this._recalculateTotals()
    this.dispatch("changed")
    this._triggerAutosave()
  }

  moveMarking(event) {
    if (this.#deferStructuralAction(event, "moveMarking")) return
    const marking = event.target.closest("[data-erp--estimate-editor-target='marking']")
    if (!marking || !this.#moveSibling(marking, event.params.direction, "[data-erp--estimate-editor-target='marking']")) return

    this._updateMarkingPositions()
    this.dispatch("changed")
    this._triggerAutosave()
    event.currentTarget.focus()
  }

  toggleMarking(event) {
    event.preventDefault()
    event.stopPropagation()

    const marking = event.target.closest("[data-erp--estimate-editor-target='marking']")
    const form = marking?.querySelector("[data-erp--estimate-editor-target='markingForm']")
    const willOpen = form?.classList.contains("hidden")
    this.#closeOtherMarkings(marking)
    if (marking) {
      if (willOpen) this.#syncMarkingAllocationRows(marking)
      this.#setMarkingOpen(marking, willOpen)
    }
  }

  #closeOtherMarkings(except = null) {
    this.markingTargets.forEach(marking => {
      if (marking === except) return
      this.#setMarkingOpen(marking, false)
    })
  }

  #normalizeOpenMarkings() {
    let openMarkingFound = false

    this.markingTargets.forEach(marking => {
      const form = marking.querySelector("[data-erp--estimate-editor-target='markingForm']")
      const isOpen = form && !form.classList.contains("hidden")
      const keepOpen = isOpen && !openMarkingFound
      if (keepOpen) openMarkingFound = true
      this.#setMarkingOpen(marking, keepOpen)
    })
  }

  #setMarkingOpen(marking, open) {
    marking.querySelector("[data-erp--estimate-editor-target='markingForm']")?.classList.toggle("hidden", !open)
    marking.querySelector("[data-marking-toggle]")?.setAttribute("aria-expanded", String(open))
  }

  #syncMarkingAllocationRows(marking) {
    if (!this.hasAllocationItemTemplateTarget || !this.hasAllocationSizeTemplateTarget) return

    const list = marking.querySelector("[data-marking-allocation-list]")
    const positionField = marking.querySelector("[data-erp--estimate-editor-target='markingPositionField']")
    if (!list || !positionField?.name) return

    const markingPrefix = positionField.name.replace(/\[position\]$/, "")
    const existingRows = new Map(
      [...marking.querySelectorAll("input[name*='[allocations_attributes]'][name$='[estimate_item_variant_id]']")]
        .filter(input => input.value)
        .map(input => [input.value, input.closest("[data-erp--marking-form-target='allocationRow']")])
    )
    const visibleVariantIds = new Set()
    let automaticAllocationRemoved = false
    let allocationIndex = [...marking.querySelectorAll("input[name*='[allocations_attributes]']")]
      .map(input => input.name.match(/\[allocations_attributes\]\[(\d+)\]/)?.[1])
      .filter(index => index != null)
      .reduce((maximum, index) => Math.max(maximum, parseInt(index)), -1) + 1

    this.#visible(this.itemTargets).forEach(item => {
      const itemId = item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
      if (!itemId) return

      const persistedSizes = [...item.querySelectorAll("[data-size-row]")].filter(sizeRow => {
        const persisted = Boolean(sizeRow.querySelector("input[type='hidden'][name$='[id]']")?.value)
        const destroyed = sizeRow.querySelector("input[name$='[_destroy]']")?.value === "1"
        return persisted && !destroyed
      })
      if (persistedSizes.length === 0) return

      let itemSection = [...list.querySelectorAll("[data-allocation-item]")]
        .find(section => section.dataset.allocationItemId === itemId)
      if (!itemSection) {
        list.insertAdjacentHTML(
          "beforeend",
          this.allocationItemTemplateTarget.innerHTML.replaceAll("ITEM_ID", itemId)
        )
        itemSection = list.lastElementChild
      }
      this.#fillAllocationItemSummary(itemSection, item, list.dataset.availableTemplate)

      const sizesContainer = itemSection.querySelector("[data-allocation-sizes]")
      if (!sizesContainer) return

      persistedSizes.forEach(sizeRow => {
        const variantId = sizeRow.querySelector("input[type='hidden'][name$='[id]']")?.value
        if (!variantId) return

        const availableQuantity = parseInt(sizeRow.querySelector("input[name$='[quantity]']")?.value) || 0
        const textileType = item.dataset.textileType === "white" ? "white" : "colored"
        const sizeLabel = sizeRow.querySelector("[data-size-label]")?.textContent?.trim() || ""
        const itemLabel = item.querySelector("[data-erp--estimate-editor-target='itemSummaryRef']")?.textContent?.trim()
          || item.querySelector("[data-erp--estimate-editor-target='itemSummaryDescription']")?.textContent?.trim()
          || ""
        const existingRow = existingRows.get(variantId)
        visibleVariantIds.add(variantId)

        if (existingRow) {
          if (this.#refreshAllocationRow(existingRow, {
            availableQuantity, itemLabel, sizeLabel, textileType, list
          })) automaticAllocationRemoved = true
          return
        }

        const html = this.allocationSizeTemplateTarget.innerHTML
          .replaceAll("MARKING_PREFIX", markingPrefix)
          .replaceAll("ALLOCATION_INDEX", allocationIndex++)
          .replaceAll("VARIANT_ID", variantId)
          .replaceAll("TEXTILE_TYPE", textileType)
          .replaceAll("AVAILABLE_QUANTITY", availableQuantity)
        sizesContainer.insertAdjacentHTML("beforeend", html)

        const allocationRow = sizesContainer.lastElementChild
        this.#refreshAllocationRow(allocationRow, {
          availableQuantity, itemLabel, sizeLabel, textileType, list
        })
      })
    })

    existingRows.forEach((row, variantId) => {
      if (!row || visibleVariantIds.has(variantId)) return

      const destroy = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")
      const checkbox = row.querySelector("[data-erp--marking-form-target='allocationCheckbox']")
      const quantity = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
      if (destroy) destroy.value = "1"
      if (checkbox) {
        const wasSelected = checkbox.checked
        checkbox.checked = false
        checkbox.disabled = true
        if (wasSelected) automaticAllocationRemoved = true
        if (wasSelected) checkbox.dispatchEvent(new Event("change", { bubbles: true }))
      }
      if (quantity) quantity.disabled = true
      row.classList.add("hidden")
    })

    if (visibleVariantIds.size > 0) list.querySelector("[data-allocation-empty]")?.remove()
    const automaticLinkRemoved = this.#syncAutomaticMarkingItemLinks(marking)
    this.application
      .getControllerForElementAndIdentifier(marking, "erp--marking-form")
      ?.compatibilityChanged()

    if ((automaticAllocationRemoved || automaticLinkRemoved) && marking.dataset.automaticTextileMarking === "true") {
      this.#discardEmptyAutomaticMarkings([ marking ])
    }
  }

  #refreshAllocationRow(row, { availableQuantity, itemLabel, sizeLabel, textileType, list }) {
    row.classList.remove("hidden")
    row.dataset.allocationType = textileType
    row.querySelector("input[name$='[textile_type]']")?.setAttribute("value", textileType)
    row.querySelector("[data-allocation-size]").textContent = sizeLabel
    row.querySelector("[data-allocation-label]").textContent = `${itemLabel} · ${sizeLabel}`
    row.querySelector("[data-allocation-type-label]").textContent = textileType === "white"
      ? list.dataset.whiteLabel
      : list.dataset.coloredLabel
    row.querySelector("[data-allocation-available]").textContent = this.#availableLabel(list, availableQuantity)

    const checkbox = row.querySelector("[data-erp--marking-form-target='allocationCheckbox']")
    const quantity = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
    checkbox?.removeAttribute("disabled")
    checkbox?.setAttribute("aria-label", `${itemLabel} ${sizeLabel}`)
    if (!quantity) return false

    quantity.max = String(availableQuantity)
    quantity.setAttribute("aria-label", `${itemLabel} ${sizeLabel} — maximum ${availableQuantity}`)
    const currentQuantity = parseInt(quantity.value) || 0
    if (currentQuantity <= availableQuantity) return false

    quantity.value = String(availableQuantity)
    if (availableQuantity === 0 && checkbox?.checked) {
      checkbox.checked = false
      checkbox.dispatchEvent(new Event("change", { bubbles: true }))
      return true
    } else if (!quantity.disabled) {
      quantity.dispatchEvent(new Event("input", { bubbles: true }))
    }

    return false
  }

  #fillAllocationItemSummary(section, item, availableTemplate) {
    const reference = item.querySelector("[data-erp--estimate-editor-target='itemSummaryRef']")?.textContent?.trim()
    const description = item.querySelector("[data-erp--estimate-editor-target='itemSummaryDescription']")?.textContent?.trim()
    const color = item.querySelector("[data-erp--estimate-editor-target='itemSummaryColor']")?.textContent?.trim()
    const quantity = this.#lineQuantity(item)

    section.querySelector("[data-allocation-item-title]").textContent = reference || description || ""
    section.querySelector("[data-allocation-item-description]").textContent = [ color, description ].filter(Boolean).join(" · ")
    section.querySelector("[data-allocation-item-available]").textContent = (availableTemplate || "__COUNT__")
      .replace("__COUNT__", quantity)
    const compatibility = section.querySelector("[data-erp--marking-form-target='compatibilityRow']")
    if (compatibility) {
      compatibility.dataset.customerSupplied = item.dataset.customerSupplied || "false"
      compatibility.dataset.compatibilityRules = item.dataset.compatibilityRules || "{}"
    }
  }

  #availableLabel(list, quantity) {
    return (list.dataset.availableTemplate || "__COUNT__").replace("__COUNT__", quantity)
  }

  #compatibilityRules(summary = {}) {
    return Object.fromEntries((summary.rules || []).map(rule => [
      `${rule.technique_id}:${rule.zone_id}`,
      {
        status: rule.status,
        max_width_mm: rule.max_width_mm,
        max_height_mm: rule.max_height_mm,
        operator_note: rule.operator_note,
        inherited: rule.inherited
      }
    ]))
  }

  // ── Sections ─────────────────────────────────────────────────

  addSection(event) {
    event.preventDefault()
    if (this.#deferStructuralAction(event, "addSection")) return

    const index = this.sectionIndexValue++
    const editorKey = `section-${crypto.randomUUID()}`
    this.sectionsContainerTarget.insertAdjacentHTML(
      "beforeend",
      this.sectionTemplateTarget.innerHTML
        .replace(/SECTION_INDEX/g, index)
        .replace(/SECTION_EDITOR_KEY/g, editorKey)
    )

    const newSection = this.sectionsContainerTarget.lastElementChild
    const itemsList = newSection.querySelector("[data-erp--estimate-editor-target='itemsList']")
    if (itemsList) this._initSortableForList(itemsList)

    this._updateSectionPositions()
    newSection.querySelector("input[type='text']")?.focus()
    this.dispatch("changed")
    this._triggerAutosave()
  }

  removeSection(event) {
    event.preventDefault()
    if (this.#deferStructuralAction(event, "removeSection")) return

    const section = event.target.closest("[data-erp--estimate-editor-target='section']")
    if (!section) return

    const itemsList = section.querySelector("[data-erp--estimate-editor-target='itemsList']")
    const ungroupedList = this._ungroupedItemsList()

    if (itemsList && ungroupedList) {
      ;[...itemsList.children].filter(el => el.hasAttribute("data-item-index")).forEach(item => {
        const sectionIdField = item.querySelector("[data-erp--estimate-editor-target='sectionIdField']")
        if (sectionIdField) sectionIdField.value = ""
        const sectionEditorKeyField = item.querySelector("[data-erp--estimate-editor-target='sectionEditorKeyField']")
        if (sectionEditorKeyField) sectionEditorKeyField.value = ""
        ungroupedList.appendChild(item)
      })
      this._updatePositions(ungroupedList)
    }

    this.#discard(section, "sectionDestroyField")
    this.dispatch("changed")
    this._triggerAutosave()
  }

  moveSection(event) {
    if (this.#deferStructuralAction(event, "moveSection")) return
    const section = event.target.closest("[data-erp--estimate-editor-target='section']")
    if (!section || !this.#moveSibling(section, event.params.direction, "[data-erp--estimate-editor-target='section']")) return

    this._updateSectionPositions()
    this.dispatch("changed")
    this._triggerAutosave()
    event.currentTarget.focus()
  }

  // ── Totals ───────────────────────────────────────────────────

  recalculate() {
    this._recalculateTotals()
  }

  percentageDiscountChanged() {
    const selectedValue = this.hasGlobalDiscountPercentageTarget
      ? this.globalDiscountPercentageTarget.value
      : "0"

    if (this.hasGlobalDiscountTypeTarget) {
      if (selectedValue === FIXED_DISCOUNT_OPTION) {
        this.globalDiscountTypeTarget.value = "fixed"
      } else {
        const percentage = parseFloat(selectedValue) || 0
        this.globalDiscountTypeTarget.value = percentage > 0 ? "percentage" : "none"
        if (this.hasGlobalDiscountAmountTarget) this.globalDiscountAmountTarget.value = "0"
      }
    }
    if (this.hasGlobalDiscountAmountFieldTarget) {
      this.globalDiscountAmountFieldTarget.classList.toggle("hidden", selectedValue !== FIXED_DISCOUNT_OPTION)
    }

    this._recalculateTotals()
  }

  _recalculateTotals() {
    this.#refreshTabCounts()
    let textilesQty = 0
    let textilesSubtotalCents = 0
    const taxableLines = []

    this.#visible(this.itemTargets).forEach(item => {
      const qty = this.#lineQuantity(item)
      const totalCents = this.#discountedLineTotalCents(item)

      textilesQty += qty
      textilesSubtotalCents += totalCents
      taxableLines.push({ totalCents, vatRate: this.#vatRate(item) })
    })
    const textilePricingUnavailable = this.#visible(this.itemTargets).some(item => item.dataset.pricingUnavailable === "true")
    const textilePricingStale = this.#visible(this.itemTargets).some(item => item.dataset.pricingStale === "true")

    const visibleMarkings = this.#visible(this.markingTargets)
    const structuredPricingStale = visibleMarkings.some(marking =>
      marking.dataset.pricingStale === "true" || marking.dataset.pricingUnavailable === "true")
    const markingGroups = new Map()

    visibleMarkings.forEach(marking => {
      const quantity = this.#lineQuantity(marking)
      const technique = marking.querySelector("input[name*='[technique]']")?.value || "__untyped__"
      const label = marking.querySelector("[data-erp--marking-form-target='techniqueBadge']")?.textContent?.trim() || "Marquage"
      const stale = marking.dataset.pricingStale === "true" || marking.dataset.pricingUnavailable === "true"
      const totalCents = stale ? null : this.#markingTotalCents(marking)
      const group = markingGroups.get(technique) || { label, quantity: 0, totalCents: 0, stale: false, unavailable: false }
      group.label = label
      group.quantity += quantity
      group.stale ||= stale
      group.unavailable ||= marking.dataset.pricingUnavailable === "true"
      if (totalCents != null) group.totalCents += totalCents
      markingGroups.set(technique, group)

      if (totalCents != null) {
        taxableLines.push({ totalCents, vatRate: this.#vatRate(marking) })
      }
    })
    this.#refreshMarkingsBreakdown([...markingGroups.values()])

    if (structuredPricingStale || textilePricingUnavailable || textilePricingStale) {
      const pending = textilePricingUnavailable || visibleMarkings.some(marking => marking.dataset.pricingUnavailable === "true")
        ? t("pricing_unavailable") : t("reprice_required")
      if (this.hasTotalQtyTarget) this.totalQtyTarget.textContent = t("pieces", { count: textilesQty })
      if (this.hasTotalTextilesTarget) this.totalTextilesTarget.textContent = textilePricingUnavailable || textilePricingStale ? pending : this.#currency(textilesSubtotalCents / 100)
      if (this.hasTotalItemsSubtotalTarget) this.totalItemsSubtotalTarget.textContent = pending
      if (this.hasTotalSubtotalTarget) this.totalSubtotalTarget.textContent = pending
      if (this.hasTotalVatTarget) this.totalVatTarget.textContent = pending
      if (this.hasTotalTtcTarget) this.totalTtcTarget.textContent = pending
      if (this.hasGlobalDiscountRowTarget) this.globalDiscountRowTarget.classList.add("hidden")
      return
    }

    const globalType = this.hasGlobalDiscountTypeTarget ? this.globalDiscountTypeTarget.value : "none"
    const globalPct = this.hasGlobalDiscountPercentageTarget
      ? (parseFloat(this.globalDiscountPercentageTarget.value) || 0)
      : 0
    const fixedDiscountCents = this.hasGlobalDiscountAmountTarget
      ? Math.round((parseFloat(this.globalDiscountAmountTarget.value) || 0) * 100)
      : 0
    const { itemsSubtotalCents, globalDiscountCents, subtotalCents, vatCents, totalCents } =
      calculateEstimateTotals(taxableLines, {
        discountType: globalType,
        discountPercentage: globalPct,
        discountAmountCents: fixedDiscountCents
      })

    if (this.hasTotalQtyTarget) this.totalQtyTarget.textContent = t("pieces", { count: textilesQty })
    if (this.hasTotalTextilesTarget) this.totalTextilesTarget.textContent = this.#currency(textilesSubtotalCents / 100)
    if (this.hasTotalItemsSubtotalTarget) this.totalItemsSubtotalTarget.textContent = this.#currency(itemsSubtotalCents / 100)
    if (this.hasTotalSubtotalTarget) this.totalSubtotalTarget.textContent = this.#currency(subtotalCents / 100)
    if (this.hasTotalVatTarget) this.totalVatTarget.textContent = this.#currency(vatCents / 100)
    if (this.hasTotalTtcTarget) this.totalTtcTarget.textContent = this.#currency(totalCents / 100)

    if (this.hasGlobalDiscountRowTarget) {
      this.globalDiscountRowTarget.classList.toggle("hidden", globalDiscountCents <= 0)
      if (this.hasTotalGlobalDiscountTarget) {
        this.totalGlobalDiscountTarget.textContent = `- ${this.#currency(globalDiscountCents / 100)}`
      }
    }
  }

  // ── Private ──────────────────────────────────────────────────

  #refreshTabCounts() {
    const counts = {
      textiles: this.#visible(this.itemTargets).length,
      markings: this.#visible(this.markingTargets).length
    }
    Object.entries(counts).forEach(([name, count]) => {
      const badge = this.element.querySelector(`[data-tab-count='${name}']`)
      if (badge) badge.textContent = count
    })
  }

  #markingTotalCents(marking) {
    // Grid supplements (screen setup, special ink…) have no form field but count in the total.
    if (marking.querySelector("input[type='checkbox'][name*='[complimentary]']")?.checked) return 0

    const structuredPricing = marking.getAttribute("data-erp--marking-form-structured-pricing-value") === "true"
    const overridden = marking.querySelector("input[type='checkbox'][name*='[unit_price_override]']")?.checked
    const serverTotalCents = parseInt(marking.dataset.markingTotalCents)
    if (structuredPricing && !overridden && Number.isFinite(serverTotalCents)) {
      // A mixed white/colour series cannot be reconstructed from its rounded average unit price.
      return serverTotalCents
    }

    const extrasCents = parseInt(marking.dataset.markingExtrasCents) || 0
    return this.#discountedLineTotalCents(marking) + extrasCents
  }

  #refreshMarkingsBreakdown(entries) {
    if (!this.hasMarkingsBreakdownTarget) return

    this.markingsBreakdownTarget.replaceChildren()
    entries.forEach(entry => {
      const row = document.createElement("div")
      row.className = "flex justify-between items-baseline text-sm text-black"
      const label = document.createElement("span")
      label.textContent = `${entry.label} · ${t("pieces", { count: entry.quantity })}`
      const amount = document.createElement("span")
      amount.className = "tabular-nums"
      amount.textContent = entry.unavailable ? t("pricing_unavailable") :
        (entry.stale ? t("reprice_required") : this.#currency(entry.totalCents / 100))
      row.append(label, amount)
      this.markingsBreakdownTarget.appendChild(row)
    })

    if (entries.length === 0) {
      const empty = document.createElement("p")
      empty.className = "text-sm text-black/50"
      empty.textContent = I18n.t("erp.sales.estimates.form.no_markings")
      this.markingsBreakdownTarget.appendChild(empty)
    }
  }

  #appendItem(container) {
    const index = this.itemIndexValue++
    container.insertAdjacentHTML("beforeend", this.itemTemplateTarget.innerHTML.replace(/ITEM_INDEX/g, index))

    const newItem = container.lastElementChild
    newItem.dataset.itemIndex = index

    const sectionIdField = newItem.querySelector("[data-erp--estimate-editor-target='sectionIdField']")
    if (sectionIdField) sectionIdField.value = container.dataset.sectionId || ""
    const sectionEditorKeyField = newItem.querySelector("[data-erp--estimate-editor-target='sectionEditorKeyField']")
    if (sectionEditorKeyField) sectionEditorKeyField.value = container.dataset.sectionEditorKey || ""

    return newItem
  }

  #appendMarking() {
    const index = this.markingIndexValue++
    this.markingsListTarget.insertAdjacentHTML(
      "beforeend",
      this.markingTemplateTarget.innerHTML.replace(/MARKING_INDEX/g, index)
    )

    const marking = this.markingsListTarget.lastElementChild
    marking.dataset.markingIndex = index
    const customerType = this.#currentCustomerType()
    marking.setAttribute("data-erp--marking-form-customer-type-value", customerType)
    requestAnimationFrame(() => {
      if (!marking.isConnected) return

      this.application
        .getControllerForElementAndIdentifier(marking, "erp--marking-form")
        ?.customerContextChanged(customerType)
    })
    return marking
  }

  #currentCustomerType() {
    const customer = this.element.querySelector("select[name='estimate[customer_id]']")
    return customer?.selectedOptions?.[0]?.dataset.customerType || "direct"
  }

  #consumeReplacement() {
    const item = this._pendingReplacement
    this._pendingReplacement = null
    return item?.isConnected ? item : null
  }

  #replaceCatalogueItem(item, colour, preservedVariantIds = new Set()) {
    this.#populateCatalogueItem(item, colour, true, preservedVariantIds)
  }

  #replaceCustomItem(item, selection) {
    this.#populateCustomItem(item, selection, true)
  }

  #populateCatalogueItem(item, colour, replacing = false, preservedVariantIds = new Set()) {
    const index = item.dataset.itemIndex

    this.#setField(item, "product_id", colour.product_id || "")
    this.#setField(item, "product_variant_id", colour.sizes?.[0]?.variant_id || "")
    this.#setField(item, "product_color_id", "")
    this.#setField(item, "product_colorway_id", colour.product_colorway_id || "")
    this.#setField(item, "customer_supplied", "0")
    this.#setField(item, "garment_type", "")
    this.#setField(item, "color_name", colour.color_name)
    this.#setField(item, "reference", colour.reference)
    this.#setField(item, "description", colour.description)
    this.#setField(item, "manual_price_reason", "")
    this.#setBooleanField(item, "unit_price_override", false)
    this.#setBooleanField(item, "complimentary", false)
    item.dataset.textileType = colour.pricing_class === "white" ? "white" : "colored"
    item.dataset.customerSupplied = "false"
    item.dataset.compatibilityRules = JSON.stringify(this.#compatibilityRules(colour.compatibility))

    if (colour.supplier_price_cents != null) {
      this.#setField(item, "supplier_price", (colour.supplier_price_cents / 100).toFixed(2))
    }
    this.#setUnitPrice(item, colour.unit_price_cents ?? 0)
    this.#applyCatalogueLayout(item)
    item.querySelector("[data-supplier-refresh]")?.classList.toggle("hidden", colour.supplier !== "imbretex")
    if (replacing) this.#replaceSizeRows(item, index, colour.sizes, preservedVariantIds)
    else this.#buildSizeRows(item, index, colour.sizes)
    this.#setImage(item, colour.image_url)
    this.#setProductLabel(item, colour.description)
    this.#syncQuantity(item, colour.quantity)
    this.#refreshItemSummary(item)
    item.querySelector("[data-erp--estimate-editor-target='itemForm']")?.classList.add("hidden")
  }

  #populateCustomItem(item, selection, replacing = false) {
    const {
      garment_type, garment_label, color_name, comment, sizes, quantity, description,
      unit_price_cents
    } = selection
    const index = item.dataset.itemIndex

    this.#setField(item, "product_id", "")
    this.#setField(item, "product_variant_id", "")
    this.#setField(item, "product_color_id", "")
    this.#setField(item, "product_colorway_id", "")
    this.#setField(item, "reference", "")
    this.#setField(item, "customer_supplied", "1")
    this.#setField(item, "garment_type", garment_type)
    this.#setField(item, "color_name", color_name)
    this.#setField(item, "description",
      description || [ garment_label, color_name, comment ].filter(Boolean).join(" — "))
    this.#setField(item, "manual_price_reason", "")
    this.#setBooleanField(item, "unit_price_override", false)
    this.#setBooleanField(item, "complimentary", false)
    item.dataset.textileType = /blanc|white/i.test(color_name || "") ? "white" : "colored"
    item.dataset.customerSupplied = "true"
    item.dataset.compatibilityRules = "{}"
    this.#setUnitPrice(item, unit_price_cents ?? 0)
    this.#applyCustomerSuppliedLayout(item)
    item.querySelector("[data-supplier-refresh]")?.classList.add("hidden")
    if (replacing) this.#replaceSizeRows(item, index, sizes)
    else this.#buildSizeRows(item, index, sizes)
    this.#setImage(item, null)
    this.#setProductLabel(item, "")
    this.#syncQuantity(item, quantity)
    this.#refreshItemSummary(item)
    item.querySelector("[data-erp--estimate-editor-target='itemForm']")?.classList.add("hidden")
  }

  #destroyAllocationsForVariantIds(variantIds) {
    if (variantIds.size === 0) return

    this.markingTargets.forEach(marking => {
      marking.querySelectorAll("[data-erp--marking-form-target='allocationRow']").forEach(row => {
        const variantId = row.querySelector("input[name$='[estimate_item_variant_id]']")?.value
        if (!variantIds.has(variantId)) return

        const destroyField = row.querySelector("input[name$='[_destroy]']")
        if (destroyField) destroyField.value = "1"
        row.style.display = "none"
      })
    })
  }

  #itemHasAllocations(item) {
    const variantIds = new Set(
      [...item.querySelectorAll("[data-size-row] input[type='hidden'][name$='[id]']")]
        .map(input => input.value)
        .filter(Boolean)
    )
    return [...this.element.querySelectorAll("[data-erp--marking-form-target='allocationRow']")].some(row => {
      const variantId = row.querySelector("input[name$='[estimate_item_variant_id]']")?.value
      const destroyed = row.querySelector("input[name$='[_destroy]']")?.value === "1"
      return variantIds.has(variantId) && !destroyed
    })
  }

  // A persisted row must be kept in the DOM and flagged for destruction; a brand new one can
  // simply go away.
  #discard(element, destroyTargetName) {
    const idField = element.querySelector("input[name*='[id]']")

    if (idField?.value) {
      const destroyField = element.querySelector(`[data-erp--estimate-editor-target='${destroyTargetName}']`)
      if (destroyField) destroyField.value = "1"
      element.classList.add("hidden")
      element.style.display = "none"
    } else {
      element.remove()
    }
  }

  // A catalogue size points at a product variant; a customer-supplied one carries its label only.
  #buildSizeRows(item, itemIndex, sizes) {
    const list = item.querySelector("[data-erp--estimate-editor-target='sizeList']")
    if (!list || !this.hasSizeTemplateTarget || !sizes?.length) return

    list.replaceChildren()
    sizes.forEach((size, position) => {
      const prefix = `estimate[items_attributes][${itemIndex}][item_variants_attributes][${position}]`
      list.insertAdjacentHTML("beforeend", this.sizeTemplateTarget.innerHTML.replace(/SIZE_PREFIX/g, prefix))

      const row = list.lastElementChild
      const label = size.size || size.size_label

      row.querySelector("[data-size-variant-id]").value = size.variant_id || ""
      row.querySelector("[data-size-canonical-id]").value = size.canonical_size_id || ""
      row.querySelector("[data-size-label-field]").value = size.variant_id ? "" : label
      row.querySelector("[data-size-label]").textContent = label
      row.querySelector("[data-size-quantity]").value = size.quantity
      row.querySelector("[data-size-quantity]").setAttribute("aria-label", t("size_quantity", { size: label }))
      // The autosave reconciles saved size rows by position, so it has to be the real one.
      row.querySelector("input[name*='[position]']").value = position
    })
  }

  #planReplacementMoves(item, colours) {
    const sourceRows = [...item.querySelectorAll("[data-size-row]")].filter(row => {
      return row.querySelector("input[type='hidden'][name$='[id]']")?.value &&
        row.querySelector("input[name$='[_destroy]']")?.value !== "1"
    })
    const usedRows = new Set()
    const moves = new Map()

    colours.forEach((colour, groupIndex) => {
      colour.sizes.forEach((size, sizeIndex) => {
        const sourceRow = this.#matchingSizeRow(sourceRows, usedRows, size)
        if (!sourceRow) return

        usedRows.add(sourceRow)
        if (groupIndex === 0) return

        const groupMoves = moves.get(groupIndex) || []
        groupMoves.push({
          sourceId: sourceRow.querySelector("input[type='hidden'][name$='[id]']").value,
          sizeIndex
        })
        moves.set(groupIndex, groupMoves)
      })
    })

    return moves
  }

  #matchingSizeRow(rows, usedRows, size) {
    const canonicalId = String(size.canonical_size_id || "")
    const label = String(size.size || size.size_label || "").trim().toLowerCase()

    return rows.find(row => {
      if (usedRows.has(row)) return false

      const existingCanonicalId = row.querySelector("[data-size-canonical-id]")?.value || ""
      const existingLabel = row.querySelector("[data-size-label]")?.textContent?.trim().toLowerCase()
      if (canonicalId && existingCanonicalId === canonicalId) return true
      return existingLabel === label
    })
  }

  #bindReplacementMoves(item, moves) {
    const rows = [...item.querySelectorAll("[data-size-row]")]
    moves.forEach(move => {
      const field = rows[move.sizeIndex]?.querySelector("[data-replacement-source-variant-id]")
      if (field) field.value = move.sourceId
    })
  }

  #replaceSizeRows(item, itemIndex, sizes, preservedVariantIds = new Set()) {
    const list = item.querySelector("[data-erp--estimate-editor-target='sizeList']")
    if (!list || !this.hasSizeTemplateTarget || !sizes?.length) return

    let nestedIndex = 0
    const activeRows = [...list.querySelectorAll("[data-size-row]")].filter(row => {
      row.querySelectorAll("input[name]").forEach(input => {
        const match = input.name.match(/\[item_variants_attributes\]\[(\d+)\]/)
        if (match) nestedIndex = Math.max(nestedIndex, parseInt(match[1]) + 1)
      })
      return row.querySelector("input[name$='[_destroy]']")?.value !== "1"
    })
    const usedRows = new Set()

    sizes.forEach((size, position) => {
      const label = size.size || size.size_label
      const canonicalId = String(size.canonical_size_id || "")
      const matchingRow = activeRows.find(row => {
        if (usedRows.has(row)) return false

        const existingCanonicalId = row.querySelector("[data-size-canonical-id]")?.value || ""
        const existingLabel = row.querySelector("[data-size-label]")?.textContent?.trim().toLowerCase()
        if (canonicalId && existingCanonicalId === canonicalId) return true
        return existingLabel === String(label || "").trim().toLowerCase()
      })

      if (matchingRow) {
        usedRows.add(matchingRow)
        matchingRow.querySelector("[data-size-variant-id]").value = size.variant_id || ""
        matchingRow.querySelector("[data-size-canonical-id]").value = canonicalId
        matchingRow.querySelector("[data-size-label-field]").value = size.variant_id ? "" : label
        matchingRow.querySelector("[data-size-label]").textContent = label
        matchingRow.querySelector("[data-size-quantity]").value = size.quantity
        matchingRow.querySelector("[data-size-quantity]").setAttribute("aria-label", t("size_quantity", { size: label }))
        matchingRow.querySelector("input[name*='[position]']").value = position
        matchingRow.querySelector("input[name$='[_destroy]']").value = "0"
        const replacementField = matchingRow.querySelector("[data-replacement-source-variant-id]")
        if (replacementField) replacementField.value = ""
        matchingRow.style.display = ""
        return
      }

      const prefix = `estimate[items_attributes][${itemIndex}][item_variants_attributes][${nestedIndex++}]`
      list.insertAdjacentHTML("beforeend", this.sizeTemplateTarget.innerHTML.replace(/SIZE_PREFIX/g, prefix))

      const row = list.lastElementChild
      row.querySelector("[data-size-variant-id]").value = size.variant_id || ""
      row.querySelector("[data-size-canonical-id]").value = canonicalId
      row.querySelector("[data-size-label-field]").value = size.variant_id ? "" : label
      row.querySelector("[data-size-label]").textContent = label
      row.querySelector("[data-size-quantity]").value = size.quantity
      row.querySelector("[data-size-quantity]").setAttribute("aria-label", t("size_quantity", { size: label }))
      row.querySelector("input[name*='[position]']").value = position
    })

    const removedVariantIds = new Set()
    activeRows.filter(row => !usedRows.has(row)).forEach(row => {
      const id = row.querySelector("input[type='hidden'][name$='[id]']")?.value
      if (id) {
        if (!preservedVariantIds.has(id)) removedVariantIds.add(id)
        row.querySelector("input[name$='[_destroy]']").value = "1"
        row.style.display = "none"
      } else {
        row.remove()
      }
    })
    this.#destroyAllocationsForVariantIds(removedVariantIds)
  }

  #sizeRowById(id) {
    return [...this.element.querySelectorAll("[data-size-row]")].find(row => {
      return row.querySelector("input[type='hidden'][name$='[id]']")?.value === String(id)
    })
  }

  #ensureAllocationItemSection(marking, item) {
    const itemId = item.dataset.itemId || item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
    const list = marking.querySelector("[data-marking-allocation-list]")
    if (!itemId || !list) return null

    let section = [...list.querySelectorAll("[data-allocation-item]")]
      .find(candidate => candidate.dataset.allocationItemId === itemId)
    if (section) return section

    list.insertAdjacentHTML(
      "beforeend",
      this.allocationItemTemplateTarget.innerHTML.replaceAll("ITEM_ID", itemId)
    )
    section = list.lastElementChild
    this.#fillAllocationItemSummary(section, item, list.dataset.availableTemplate)
    list.querySelector("[data-allocation-empty]")?.remove()
    return section
  }

  #refreshMovedAllocationRow(row, item, sizeRow) {
    const size = sizeRow.querySelector("[data-size-label]")?.textContent?.trim() || ""
    const quantity = parseInt(sizeRow.querySelector("[data-size-quantity]")?.value) || 0
    const label = item.querySelector("[data-erp--estimate-editor-target='itemSummaryRef']")?.textContent?.trim()
      || item.querySelector("[data-erp--estimate-editor-target='itemSummaryDescription']")?.textContent?.trim()
      || ""
    const textileType = item.dataset.textileType === "white" ? "white" : "colored"
    const list = row.closest("[data-marking-allocation-list]")

    row.dataset.allocationType = textileType
    const typeField = row.querySelector("input[name$='[textile_type]']")
    if (typeField) typeField.value = textileType
    const quantityField = row.querySelector("input[name$='[quantity]']")
    if (quantityField) quantityField.max = quantity
    const checkbox = row.querySelector("[data-erp--marking-form-target='allocationCheckbox']")
    if (checkbox) checkbox.setAttribute("aria-label", `${label} ${size}`)
    const sizeLabel = row.querySelector("[data-allocation-size]")
    if (sizeLabel) sizeLabel.textContent = size
    const allocationLabel = row.querySelector("[data-allocation-label]")
    if (allocationLabel) allocationLabel.textContent = `${label} · ${size}`
    const typeLabel = row.querySelector("[data-allocation-type-label]")
    if (typeLabel) typeLabel.textContent = textileType === "white" ? list?.dataset.whiteLabel : list?.dataset.coloredLabel
    const available = row.querySelector("[data-allocation-available]")
    if (available) available.textContent = this.#availableLabel(list, quantity)
  }

  #applyCatalogueLayout(item) {
    item.querySelector("[data-item-mode='catalogue']")?.classList.remove("hidden")
    item.querySelector("[data-item-mode='customer']")?.classList.add("hidden")
    item.querySelectorAll("[data-cost-field]").forEach(field => field.classList.remove("hidden"))
    item.querySelector("[data-customer-supplied-badge]")?.classList.add("hidden")
  }

  #applyCustomerSuppliedLayout(item) {
    item.querySelector("[data-item-mode='catalogue']")?.classList.add("hidden")
    item.querySelector("[data-item-mode='customer']")?.classList.remove("hidden")
    item.querySelectorAll("[data-cost-field]").forEach(field => field.classList.add("hidden"))
    item.querySelector("[data-customer-supplied-badge]")?.classList.remove("hidden")
  }

  // The line quantity is always the sum of its sizes when a breakdown exists; a line without
  // one keeps the quantity it was given.
  #syncQuantity(item, fallbackQuantity) {
    const rows = [...item.querySelectorAll("[data-size-row]")].filter(row => {
      return row.querySelector("input[name*='[_destroy]']")?.value !== "1"
    })

    if (rows.length === 0) {
      if (fallbackQuantity) this.#setField(item, "quantity", fallbackQuantity)
      return
    }

    const total = rows.reduce((sum, row) => {
      return sum + (parseInt(row.querySelector("input[name*='[quantity]']")?.value) || 0)
    }, 0)

    const field = item.querySelector("[data-line-quantity]")
    if (field) {
      field.value = total
      field.readOnly = true
      field.classList.add("bg-black/5")
    }
  }

  #refreshItemSummary(item) {
    const value = (selector) => item.querySelector(selector)?.value || ""

    this.#setText(item, "itemSummaryRef", value("input[name*='[reference]']"))
    this.#setText(item, "itemSummaryColor", value("input[name*='[color_name]']"))
    this.#setText(item, "itemSummaryDescription", value("textarea[name*='[description]']") || t("new_line"))

    const unitPrice = parseFloat(value("input[name*='[unit_price]']"))
    const pricingUnavailable = item.dataset.pricingUnavailable === "true"
    const pricingStale = item.dataset.pricingStale === "true"
    this.#setText(item, "itemSummaryUnitPrice", !pricingUnavailable && !pricingStale && Number.isFinite(unitPrice) && unitPrice > 0 ? this.#currency(unitPrice) : "—")

    const total = this.#discountedLineTotal(item)
    this.#setText(item, "itemSummaryTotal", pricingUnavailable ? t("pricing_unavailable") : (pricingStale ? t("reprice_required") : (total > 0 ? this.#currency(total) : "—")))
    const discountType = value("select[name*='[discount_type]']") || "percentage"
    const discountPercentage = parseInt(value("select[name*='[discount_percentage]']")) || 0
    const fixedDiscount = parseFloat(value("input[name*='[discount_amount]']")) || 0
    const discountSummary = item.querySelector("[data-item-discount-summary]")
    if (discountSummary) {
      discountSummary.textContent = discountType === "fixed" && fixedDiscount > 0
        ? `−${this.#currency(fixedDiscount)}`
        : (discountPercentage > 0 ? `−${discountPercentage} %` : "—")
    }

    const chips = item.querySelector("[data-erp--estimate-editor-target='itemSummarySizes']")
    if (chips) {
      chips.replaceChildren()
      item.querySelectorAll("[data-size-row]").forEach(row => {
        if (row.querySelector("input[name*='[_destroy]']")?.value === "1") return
        const quantity = parseInt(row.querySelector("input[name*='[quantity]']")?.value) || 0
        if (quantity <= 0) return

        const chip = document.createElement("span")
        chip.className = "inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-black/5 border border-black rounded"
        chip.textContent = `${row.querySelector("[data-size-label]")?.textContent || ""}×${quantity}`
        chips.appendChild(chip)
      })
    }
  }

  // Mirrors EstimateItems::Computer#discounted_total_cents: percentage discounts are
  // rounded once on the complete line, never truncated once per piece.
  #discountedLineTotal(line) {
    return this.#discountedLineTotalCents(line) / 100
  }

  #discountedLineTotalCents(line) {
    if (line.dataset.pricingUnavailable === "true" || line.dataset.pricingStale === "true") return 0
    if (line.querySelector("input[type='checkbox'][name*='[complimentary]']")?.checked) return 0

    const quantity = this.#lineQuantity(line)
    const unitCents = Math.round((parseFloat(line.querySelector("input[name*='[unit_price]']")?.value) || 0) * 100)
    const discountType = line.querySelector("select[name*='[discount_type]']")?.value || "percentage"
    const discountPct = parseInt(line.querySelector("select[name*='[discount_percentage]']")?.value) || 0
    const discountCents = Math.round((parseFloat(line.querySelector("input[name*='[discount_amount]']")?.value) || 0) * 100)

    if (discountType === "fixed") return Math.max(0, unitCents - discountCents) * quantity
    if (discountPct > 0) return Math.round(unitCents * quantity * (100 - discountPct) / 100)
    return unitCents * quantity
  }

  // The line's own quantity input, never one of the nested size or attachment quantities.
  #lineQuantity(line) {
    return parseInt(line.querySelector("[data-line-quantity]")?.value) || 0
  }

  #vatRate(line) {
    return line.querySelector("select[name*='[vat_rate]']")?.value || "vat_20"
  }

  #setField(element, fieldName, value) {
    if (value === undefined || value === null) return

    const field = element.querySelector(`input[name*='[${fieldName}]'], select[name*='[${fieldName}]'], textarea[name*='[${fieldName}]']`)
    if (!field) return

    field.value = value
    field.dispatchEvent(new Event("input", { bubbles: true }))
    field.dispatchEvent(new Event("change", { bubbles: true }))
  }

  #setText(element, targetName, text) {
    const node = element.querySelector(`[data-erp--estimate-editor-target='${targetName}']`)
    if (node) node.textContent = text
  }

  #setImage(item, imageUrl) {
    const container = item.querySelector("[data-variant-image]")
    if (!container) return

    container.replaceChildren()
    if (!imageUrl) {
      const placeholder = document.createElement("div")
      placeholder.className = "w-9 h-9 rounded border border-black bg-black/5 flex items-center justify-center"
      const icon = document.createElement("i")
      icon.className = "lni lni-gallery text-black/20"
      placeholder.appendChild(icon)
      container.appendChild(placeholder)
      return
    }

    const img = document.createElement("img")
    img.addEventListener("error", () => this.#setImage(item, null), { once: true })
    img.src = imageUrl
    img.loading = "lazy"
    img.className = "w-9 h-9 object-cover rounded border border-black"
    container.appendChild(img)
  }

  #setUnitPrice(item, cents) {
    if (cents == null) return

    this.#setField(item, "unit_price", (cents / 100).toFixed(2))
  }

  #setBooleanField(item, fieldName, checked) {
    const checkbox = item.querySelector(`input[type='checkbox'][name*='[${fieldName}]']`)
    if (checkbox) {
      checkbox.checked = checked
      checkbox.dispatchEvent(new Event("change", { bubbles: true }))
      return
    }

    const anchor = item.querySelector("input[name*='[unit_price]']")
    if (!anchor) return

    let hidden = item.querySelector(`input[type='hidden'][data-replacement-field='${fieldName}']`)
    if (!hidden) {
      hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = anchor.name.replace(/\[unit_price\]$/, `[${fieldName}]`)
      hidden.dataset.replacementField = fieldName
      item.prepend(hidden)
    }
    hidden.value = checked ? "1" : "0"
  }

  #setProductLabel(item, label) {
    const node = item.querySelector("[data-product-label]")
    if (node) node.textContent = label
  }

  #visible(elements) {
    return elements.filter(element => element.style.display !== "none")
  }

  #moveSibling(element, direction, selector) {
    if (this.#structuralLocked()) return false

    const siblings = this.#visible([...element.parentElement.children].filter(candidate => candidate.matches(selector)))
    const index = siblings.indexOf(element)
    const target = siblings[index + (direction === "up" ? -1 : 1)]
    if (!target) return false

    if (direction === "up") element.parentElement.insertBefore(element, target)
    else element.parentElement.insertBefore(target, element)
    return true
  }

  #structuralLocked() {
    return this.element.closest("form")?.dataset.saveState === "saving"
  }

  #deferStructuralAction(event, actionName, persist = true) {
    if (!this.#structuralLocked()) return false

    const snapshot = {
      target: event.target,
      currentTarget: event.currentTarget,
      params: { ...(event.params || {}) },
      detail: event.detail
    }
    this._queuedStructuralActions.push({ actionName, persist, snapshot })
    this.element.dispatchEvent(new CustomEvent("estimate-editor:structural-queued", { bubbles: true }))
    return true
  }

  #handleAutosaveRequestFinished(event) {
    if (!event.detail?.successful) return

    this.#completeTextileMarkingFlow()
    if (this._queuedStructuralActions.length === 0) return

    const queued = this._queuedStructuralActions.splice(0)
    let shouldPersist = false
    queued.forEach(({ actionName, persist, snapshot }) => {
      const control = snapshot.currentTarget
      if (control instanceof Element && !control.isConnected) return

      this[actionName]({
        preventDefault() {},
        stopPropagation() {},
        ...snapshot
      })
      shouldPersist ||= persist
    })

    if (shouldPersist) this._autosaveForm?.requestSubmit()
  }

  #scheduleTextileMarkingFlow(items) {
    // Allocations reference persisted size rows. Save the selected textiles first, then
    // create the marking once the autosave response has populated those ids in the DOM.
    if (!this._autosaveForm?.matches("[data-controller~='erp--autosave']")) {
      this._pendingTextileMarkingItems = null
      return
    }

    const pendingItems = (this._pendingTextileMarkingItems || []).filter(item => item?.isConnected)
    const selectedItems = items.filter(item => item?.isConnected)
    this._pendingTextileMarkingItems = [ ...new Set([ ...pendingItems, ...selectedItems ]) ]
    this._triggerAutosave()
    this._autosaveForm?.requestSubmit()
  }

  #cancelPendingTextileMarkingItem(item) {
    if (!this._pendingTextileMarkingItems) return

    const remainingItems = this._pendingTextileMarkingItems.filter(candidate => candidate !== item && candidate.isConnected)
    this._pendingTextileMarkingItems = remainingItems.length > 0 ? remainingItems : null
  }

  #automaticMarkingsAllocatedToItem(item) {
    const itemId = item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
    const variantIds = new Set(
      [...item.querySelectorAll("[data-size-row] input[type='hidden'][name$='[id]']")]
        .map(input => input.value)
        .filter(Boolean)
    )

    return this.markingTargets.filter(marking => {
      if (marking.dataset.automaticTextileMarking !== "true") return false

      const allocatedBySize = [...marking.querySelectorAll("[data-erp--marking-form-target='allocationRow']")].some(row => {
        const variantId = row.querySelector("input[name$='[estimate_item_variant_id]']")?.value
        const destroyed = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")?.value === "1"
        return variantIds.has(variantId) && !destroyed
      })
      const linkedWithoutSizes = itemId && [...marking.querySelectorAll("[data-automatic-marking-item-id]")].some(row => {
        const destroyed = row.querySelector("[data-erp--marking-form-target='linkDestroy']")?.value === "1"
        return row.dataset.automaticMarkingItemId === itemId && !destroyed
      })

      return allocatedBySize || linkedWithoutSizes
    })
  }

  #discardEmptyAutomaticMarkings(markings) {
    markings.forEach(marking => {
      const hasAllocation = [...marking.querySelectorAll("[data-erp--marking-form-target='allocationRow']")].some(row => {
        return row.querySelector("[data-erp--marking-form-target='allocationDestroy']")?.value !== "1"
      })
      const hasLegacyLink = [...marking.querySelectorAll("[data-automatic-marking-item-id]")].some(row => {
        return row.querySelector("[data-erp--marking-form-target='linkDestroy']")?.value !== "1"
      })
      if (!hasAllocation && !hasLegacyLink) this.#discard(marking, "markingDestroyField")
    })
    this._updateMarkingPositions()
  }

  #completeTextileMarkingFlow() {
    const items = (this._pendingTextileMarkingItems || []).filter(item => item.isConnected)
    if (items.length === 0 || !this.hasMarkingsListTarget) {
      this._pendingTextileMarkingItems = null
      return
    }

    const variantIds = items.flatMap(item => {
      return [...item.querySelectorAll("[data-size-row]")]
        .filter(row => row.querySelector("input[name$='[_destroy]']")?.value !== "1")
        .filter(row => (parseInt(row.querySelector("input[name$='[quantity]']")?.value) || 0) > 0)
        .map(row => row.querySelector("input[type='hidden'][name$='[id]']")?.value)
        .filter(Boolean)
    })
    const sizeLessItems = items.filter(item => {
      const itemId = item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
      const activeSizeRows = [...item.querySelectorAll("[data-size-row]")].filter(row => {
        return row.querySelector("input[name$='[_destroy]']")?.value !== "1"
      })
      return itemId && activeSizeRows.length === 0 && this.#lineQuantity(item) > 0
    })

    this._pendingTextileMarkingItems = null
    if (variantIds.length === 0 && sizeLessItems.length === 0) return

    this.#closeOtherMarkings()
    const markings = []
    if (variantIds.length > 0) {
      const marking = this.#appendMarking()
      marking.dataset.automaticTextileMarking = "true"
      this.#syncMarkingAllocationRows(marking)
      this.#selectMarkingAllocations(marking, variantIds)
      markings.push(marking)
    }
    if (sizeLessItems.length > 0) {
      const marking = this.#appendMarking()
      marking.dataset.automaticTextileMarking = "true"
      this.#syncMarkingAllocationRows(marking)
      this.#appendAutomaticMarkingItemLinks(marking, sizeLessItems)
      markings.push(marking)
    }

    const markingToOpen = markings[markings.length - 1]
    markings.forEach(marking => this.#setMarkingOpen(marking, marking === markingToOpen))

    this._updateMarkingPositions()
    this._recalculateTotals()
    this.dispatch("changed")
    this._triggerAutosave()
    this.#openMarkingsTab(markingToOpen)
  }

  #appendAutomaticMarkingItemLinks(marking, items) {
    const positionField = marking.querySelector("[data-erp--estimate-editor-target='markingPositionField']")
    if (!positionField?.name) return

    const markingPrefix = positionField.name.replace(/\[position\]$/, "")
    const container = document.createElement("div")
    container.hidden = true
    container.dataset.automaticMarkingLinks = ""

    items.forEach((item, index) => {
      const itemId = item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
      if (!itemId) return

      const row = document.createElement("div")
      row.dataset.automaticMarkingItemId = itemId
      row.setAttribute("data-erp--marking-form-target", "linkRow")

      const appendHiddenField = (attribute, value, target) => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = `${markingPrefix}[marking_items_attributes][${index}][${attribute}]`
        input.value = value
        if (target) input.setAttribute("data-erp--marking-form-target", target)
        row.appendChild(input)
      }
      appendHiddenField("estimate_item_id", itemId)
      appendHiddenField("quantity", this.#lineQuantity(item), "linkQuantity")
      appendHiddenField("_destroy", "0", "linkDestroy")

      const label = document.createElement("span")
      label.dataset.linkLabel = ""
      label.textContent = item.querySelector("[data-erp--estimate-editor-target='itemSummaryRef']")?.textContent?.trim()
        || item.querySelector("[data-erp--estimate-editor-target='itemSummaryDescription']")?.textContent?.trim()
        || ""
      row.appendChild(label)
      container.appendChild(row)
    })

    marking.appendChild(container)
    this.#refreshAutomaticMarkingItemLinks(marking)
  }

  #syncAutomaticMarkingItemLinks(marking) {
    let removed = false
    const rows = [...marking.querySelectorAll("[data-automatic-marking-item-id]")]
    if (rows.length === 0) return removed

    const itemsById = new Map(this.#visible(this.itemTargets).map(item => {
      const itemId = item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
      return [ itemId, item ]
    }).filter(([ itemId ]) => itemId))

    rows.forEach(row => {
      const item = itemsById.get(row.dataset.automaticMarkingItemId)
      const quantity = item ? this.#lineQuantity(item) : 0
      const quantityField = row.querySelector("[data-erp--marking-form-target='linkQuantity']")
      const destroyField = row.querySelector("[data-erp--marking-form-target='linkDestroy']")

      if (quantity > 0) {
        if (quantityField) quantityField.value = quantity
        return
      }

      if (destroyField?.value !== "1") removed = true
      if (destroyField) destroyField.value = "1"
    })

    this.#refreshAutomaticMarkingItemLinks(marking)
    return removed
  }

  #refreshAutomaticMarkingItemLinks(marking) {
    const controller = this.application.getControllerForElementAndIdentifier(marking, "erp--marking-form")
    if (controller) {
      controller.quantityChanged()
      return
    }

    requestAnimationFrame(() => this.application
      .getControllerForElementAndIdentifier(marking, "erp--marking-form")
      ?.quantityChanged())
  }

  #selectMarkingAllocations(marking, variantIds) {
    const selectedVariantIds = new Set(variantIds)

    marking.querySelectorAll("[data-erp--marking-form-target='allocationRow']").forEach(row => {
      const variantId = row.querySelector("input[name$='[estimate_item_variant_id]']")?.value
      if (!selectedVariantIds.has(variantId)) return

      const checkbox = row.querySelector("[data-erp--marking-form-target='allocationCheckbox']")
      const destroy = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")
      const quantity = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
      if (checkbox) checkbox.checked = true
      if (destroy) destroy.value = "0"
      if (quantity) {
        quantity.disabled = false
        quantity.value = quantity.max || quantity.value || "1"
      }
    })
  }

  #openMarkingsTab(marking) {
    const tabs = marking.closest("[data-controller~='erp--tabs']")
    const tab = tabs?.querySelector("[data-erp--tabs-target='tab'][data-tab-name='markings']")
    tab?.click()

    requestAnimationFrame(() => {
      marking.querySelector("[data-erp--marking-form-target='canonicalTechnique']")?.focus()
    })
  }

  #currency(amount) {
    return formatCurrency(amount)
  }

  // ── SortableJS ───────────────────────────────────────────────

  _initSortable() {
    if (this.hasSectionsContainerTarget) {
      Sortable.create(this.sectionsContainerTarget, {
        handle: ".section-drag-handle",
        draggable: "[data-section-index]",
        animation: 150,
        emptyInsertThreshold: 0,
        onMove: () => !this.#structuralLocked(),
        onEnd: () => {
          this._updateSectionPositions()
          this.dispatch("changed")
        }
      })
    }

    this.itemsListTargets.forEach(list => this._initSortableForList(list))
  }

  _initSortableForList(list) {
    if (list._sortable) return

    list._sortable = Sortable.create(list, {
      group: "items",
      handle: ".drag-handle",
      draggable: "[data-item-index]",
      animation: 150,
      fallbackOnBody: true,
      swapThreshold: 0.65,
      onMove: () => !this.#structuralLocked(),
      onEnd: (evt) => {
        this._updatePositions(evt.to)
        if (evt.from !== evt.to) this._updatePositions(evt.from)
        this._updateSectionIdForItem(evt.item, evt.to)
        this.dispatch("changed")
      }
    })
  }

  _updatePositions(container) {
    this.#visible([...container.children].filter(el => el.hasAttribute("data-item-index")))
      .forEach((item, index) => this.#setPosition(item, "positionField", index))
  }

  _updateMarkingPositions() {
    this.#visible([...this.markingsListTarget.children].filter(el => el.hasAttribute("data-marking-index")))
      .forEach((marking, index) => this.#setPosition(marking, "markingPositionField", index))
  }

  _updateSectionPositions() {
    this.#visible([...this.sectionsContainerTarget.children].filter(el => el.hasAttribute("data-section-index")))
      .forEach((section, index) => this.#setPosition(section, "sectionPositionField", index))
  }

  #setPosition(element, targetName, index) {
    const field = element.querySelector(`[data-erp--estimate-editor-target='${targetName}']`)
    if (!field) return

    field.value = index
    field.dispatchEvent(new Event("change", { bubbles: true }))
  }

  _updateSectionIdForItem(itemEl, container) {
    const field = itemEl.querySelector("[data-erp--estimate-editor-target='sectionIdField']")
    if (field) field.value = container.dataset.sectionId || ""
    const editorKeyField = itemEl.querySelector("[data-erp--estimate-editor-target='sectionEditorKeyField']")
    if (editorKeyField) editorKeyField.value = container.dataset.sectionEditorKey || ""
  }

  _ungroupedItemsList() {
    return this.itemsListTargets.find(list => list.dataset.sectionId === "")
  }

  // The section the picker was opened from, unless it has since been removed from the DOM.
  _pickerContainer() {
    const container = this._pendingContainer
    this._pendingContainer = null

    return container?.isConnected ? container : this._ungroupedItemsList()
  }

  _triggerAutosave() {
    this.element.dispatchEvent(new CustomEvent("estimate-editor:autosave", { bubbles: true }))
  }
}
